import React from "react";
import { Box, Button, Stack, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import { TaskEditorState } from "./utils/taskEditorUtils";

interface TaskEditorLivePreviewProps {
  task?: TaskEditorState;
}

// 임시 미리보기 — 활동지 JSON 전체를 그대로 표시.
// 시각 렌더 미리보기는 활동지 v2 contract 위에서 다시 설계 예정.
// (.claude/skills/task-editor/SKILL.md 참조)
function TaskEditorLivePreview({ task }: TaskEditorLivePreviewProps) {
  const [copyState, setCopyState] = React.useState<"idle" | "copied">("idle");
  const json = task ? JSON.stringify(task, null, 2) : "";

  const handleCopy = async () => {
    if (!json) return;
    try {
      await navigator.clipboard.writeText(json);
      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 1500);
    } catch {
      // 클립보드 권한 없으면 조용히 무시
    }
  };

  if (!task) {
    return (
      <Box
        sx={{
          p: 3,
          borderRadius: "12px",
          border: "1px dashed",
          borderColor: theme.vars.palette.Border.Weak,
          backgroundColor: theme.vars.palette.Background.Level1,
          textAlign: "center",
        }}
      >
        <Typography
          variant="Footnote"
          sx={{
            color: theme.vars.palette.Content.Weak,
            fontSize: "12px",
          }}
        >
          활동지를 선택하거나 새로 만들면 JSON 이 표시됩니다.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1} sx={{ height: "100%", minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ flexShrink: 0 }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography
            variant="Footnote"
            sx={{
              fontWeight: 700,
              fontSize: "13px",
              color: theme.vars.palette.Content.Strong,
            }}
          >
            활동지 JSON
          </Typography>
          <Typography
            variant="Footnote"
            sx={{
              fontSize: "11px",
              color: theme.vars.palette.Content.Weak,
            }}
          >
            시각 렌더 미리보기는 준비 중입니다
          </Typography>
        </Stack>
        <Button
          size="sm"
          variant="soft"
          color="neutral"
          onClick={handleCopy}
          data-testid="task-editor-live-preview-copy"
        >
          {copyState === "copied" ? "복사됨!" : "JSON 복사"}
        </Button>
      </Stack>
      <Box
        component="pre"
        data-testid="task-editor-live-preview-json"
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          backgroundColor: theme.vars.palette.Background.Level1,
          border: "1px solid",
          borderColor: theme.vars.palette.Border.Weak,
          borderRadius: "8px",
          p: 1.5,
          m: 0,
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          fontSize: "12px",
          lineHeight: 1.55,
          color: theme.vars.palette.Content.Strong,
          whiteSpace: "pre",
          tabSize: 2,
        }}
      >
        {json}
      </Box>
    </Stack>
  );
}

export default TaskEditorLivePreview;
